import { useState, useMemo, useEffect } from 'react';
import PropertyCard from './components/PropertyCard';
import FiltersPanel from './components/FiltersPanel';
import { properties } from '../../mocks/properties';

interface Filters {
  type: string;
  minPrice: number;
  maxPrice: number;
  bedrooms: number;
  location: string;
  features: string[];
  minArea: number;
  maxArea: number;
}

const initialFilters: Filters = {
  type: '',
  minPrice: 100000,
  maxPrice: 5000000,
  bedrooms: 0,
  location: '',
  features: [],
  minArea: 0,
  maxArea: 0
};

const ImoveisPage = () => {
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [sortBy, setSortBy] = useState('recent');
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  useEffect(() => {
    document.title = 'Imóveis à Venda e para Alugar em SP | Daniel Carvalho Imobiliário';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Encontre imóveis à venda e para alugar em São Paulo e região. Apartamentos, casas, coberturas e lançamentos com as melhores condições de financiamento imobiliário.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL ?? 'https://danielcarvalhoimobiliario.com.br'}/imoveis`);

    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://danielcarvalhoimobiliario.com.br';
    const ogImage = 'https://static.readdy.ai/image/b38d0475ce589291eceb67661996448f/3c37db76e3e907da977a289ded40f172.png';
    const ogTitle = 'Imóveis à Venda e para Alugar em SP | Daniel Carvalho Imobiliário';
    const ogDesc = 'Encontre imóveis à venda e para alugar em São Paulo e região. Apartamentos, casas, coberturas e lançamentos com as melhores condições de financiamento imobiliário.';

    const setMeta = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); document.head.appendChild(el); }
      el.setAttribute(attr, val);
    };

    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:url"]', 'content', `${siteUrl}/imoveis`);
    setMeta('meta[property="og:title"]', 'content', ogTitle);
    setMeta('meta[property="og:description"]', 'content', ogDesc);
    setMeta('meta[property="og:image"]', 'content', ogImage);
    setMeta('meta[property="og:image:width"]', 'content', '1200');
    setMeta('meta[property="og:image:height"]', 'content', '630');
    setMeta('meta[property="og:locale"]', 'content', 'pt_BR');
    setMeta('meta[property="og:site_name"]', 'content', 'Daniel Carvalho Serviços Imobiliários');
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', ogTitle);
    setMeta('meta[name="twitter:description"]', 'content', ogDesc);
    setMeta('meta[name="twitter:image"]', 'content', ogImage);

    const schemaId = 'schema-imoveis';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Imóveis à Venda e para Alugar – Daniel Carvalho',
        url: `${import.meta.env.VITE_SITE_URL ?? 'https://danielcarvalhoimobiliario.com.br'}/imoveis`,
        description: 'Galeria de imóveis disponíveis para venda e locação em São Paulo e região.',
        provider: {
          '@type': 'RealEstateAgent',
          name: 'Daniel Carvalho Serviços Imobiliários',
          telephone: '+55-11-92230-5730'
        }
      });
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const filteredProperties = useMemo(() => {
    let result = properties.filter((property) => {
      if (filters.type && property.type !== filters.type) return false;
      if (property.price < filters.minPrice || property.price > filters.maxPrice) return false;
      if (filters.bedrooms > 0) {
        if (filters.bedrooms === 5) {
          if (property.bedrooms < 5) return false;
        } else {
          if (property.bedrooms !== filters.bedrooms) return false;
        }
      }
      if (filters.location && !property.location.includes(filters.location)) return false;
      if (filters.features.length > 0) {
        const hasAllFeatures = filters.features.every(f => property.features.includes(f));
        if (!hasAllFeatures) return false;
      }
      if (filters.minArea > 0 && property.area < filters.minArea) return false;
      if (filters.maxArea > 0 && property.area > filters.maxArea) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          property.title.toLowerCase().includes(query) ||
          property.location.toLowerCase().includes(query) ||
          property.type.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      return true;
    });

    switch (sortBy) {
      case 'price-asc': result.sort((a, b) => a.price - b.price); break;
      case 'price-desc': result.sort((a, b) => b.price - a.price); break;
      case 'area-desc': result.sort((a, b) => b.area - a.area); break;
      default: break;
    }

    return result;
  }, [filters, sortBy, searchQuery]);

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-teal-900 via-teal-800 to-emerald-900">
        <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Aerial%20view%20of%20modern%20residential%20neighborhood%20with%20houses%20and%20apartments%20urban%20landscape%20city%20skyline%20sunset%20lighting%20real%20estate%20development%20clean%20streets&width=1920&height=600&seq=hero1&orientation=landscape')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Encontre o <span className="text-teal-300">Imóvel Ideal</span>
            </h1>
            <p className="text-lg text-teal-100 mb-8">
              Explore nossa seleção exclusiva de imóveis em São Paulo e região
            </p>
            <div className="bg-white rounded-2xl p-2 shadow-xl flex flex-col md:flex-row gap-2">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Buscar por localização, tipo ou nome..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <button
                onClick={() => setIsMobileFiltersOpen(true)}
                className="md:hidden px-6 py-3.5 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-filter-3-line"></i>
                Filtros
              </button>
              <button className="hidden md:flex px-8 py-3.5 bg-teal-600 text-white rounded-xl font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap items-center gap-2">
                <i className="ri-search-line"></i>
                Buscar
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="hidden lg:block w-80 flex-shrink-0">
              <FiltersPanel filters={filters} setFilters={setFilters} onClearFilters={handleClearFilters} />
            </aside>
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <p className="text-gray-600 dark:text-gray-300">
                  <strong className="text-gray-900 dark:text-white">{filteredProperties.length}</strong> imóveis encontrados
                </p>
                <div className="flex items-center gap-3">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                  >
                    <option value="recent">Mais Recentes</option>
                    <option value="price-asc">Menor Preço</option>
                    <option value="price-desc">Maior Preço</option>
                    <option value="area-desc">Maior Área</option>
                  </select>
                </div>
              </div>
              {filteredProperties.length > 0 ? (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6" data-product-shop>
                  {filteredProperties.map((property) => (
                    <PropertyCard key={property.id} property={property} />
                  ))}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center">
                  <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-home-smile-line text-4xl text-gray-400 dark:text-gray-500"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Nenhum imóvel encontrado</h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-6">Tente ajustar os filtros para encontrar mais opções</p>
                  <button
                    onClick={handleClearFilters}
                    className="px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Limpar Filtros
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-teal-600 to-emerald-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Não encontrou o que procura?</h2>
          <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
            Entre em contato conosco e nossa equipe irá ajudá-lo a encontrar o imóvel perfeito para você
          </p>
          <a
            href="https://wa.me/5511922305730?text=Olá,%20Daniel!%20Estou%20procurando%20um%20imóvel%20e%20gostaria%20de%20ajuda."
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-teal-600 rounded-xl font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-xl"></i>
            Falar com Especialista
          </a>
        </div>
      </section>

      {isMobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsMobileFiltersOpen(false)}></div>
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] bg-white dark:bg-gray-900 rounded-t-3xl overflow-hidden animate-slide-up">
            <FiltersPanel
              filters={filters}
              setFilters={setFilters}
              onClearFilters={handleClearFilters}
              isMobile
              onClose={() => setIsMobileFiltersOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImoveisPage;
